import { useMemo, useEffect } from 'react';
import useUrlState from './useUrlState';
import useTableSorting from './useTableSorting';
import useTablePagination from './useTablePagination';
import useTableFiltering from './useTableFiltering';
import useTableSearch from './useTableSearch';
import useTableDateRange from './useTableDateRange';
import useDataService from './useDataService';
import logger from '../utils/logger';

function useTableData({
    service,
    initialFilters = {},
    filterableColumns = [],
    initialPageSize = 10,
    serviceUpdateEvent,
    hasDateRange,
    searchableColumns = [],
    skipInitialLoad = false, // Add skipInitialLoad parameter
    initialSortColumn,
    initialSortType,
}) {
    // Initialize all hooks independently - no callbacks needed
    const urlState = useUrlState();
    const dataService = useDataService({ service, serviceUpdateEvent });
    const dateRange = useTableDateRange({ hasDateRange, ...urlState });
    const pagination = useTablePagination({ initialPageSize, ...urlState });
    const search = useTableSearch({ searchableColumns, ...urlState });
    const filtering = useTableFiltering({ 
        initialFilters, 
        filterableColumns, 
        ...urlState, 
        onFiltersChange: pagination.handleFiltersChange 
    });
    const sorting = useTableSorting({ 
        ...urlState, 
        initialSortColumn, 
        initialSortType 
    });    useEffect(() => {
        let isMounted = true; // Prevent state updates if component unmounts

        const initializeTable = async () => {
            // Skip initialization if skipInitialLoad is true
            if (skipInitialLoad) {
                return;
            }

            try {
                if (hasDateRange) {
                    // Initialize date range first
                    if (dateRange.initializeDateRange) {
                        const { initialStartDate, initialEndDate } = dateRange.initializeDateRange();

                        // Only proceed if component is still mounted
                        if (isMounted) {
                            if (initialStartDate && initialEndDate) {
                                const formattedRange = dateRange.getFormattedDateRange ? dateRange.getFormattedDateRange() : { from: null, to: null };
                                await dataService.loadInitialData(formattedRange);
                            } else {
                                await dataService.loadInitialData();
                            }
                        }
                    }
                } else {
                    // Load data immediately if no date range required
                    if (isMounted) {
                        await dataService.loadInitialData();
                    }
                }
            } catch (error) {
                if (isMounted) {
                    logger.error('Error initializing table data:', error);
                }
            }
        };

        initializeTable();

        // Cleanup function
        return () => {
            service?.clear?.(); // Clear service data if available
            isMounted = false;
        };
    }, [hasDateRange, skipInitialLoad]); // Add skipInitialLoad to dependency array

    const filtersRefreshTrigger = filtering?.filtersRefreshTrigger || 0; const processedData = useMemo(() => {
        try {
            let result = dataService.getData();

            // Update column values cache with raw data before applying any filters
            if (result && result.length > 0 && filtering?.updateColumnValuesCache) {
                filtering.updateColumnValuesCache(result);
            }

            // Apply each transformation in sequence with safety checks
            if (search && search.applySearch) {
                result = search.applySearch(result);
            }

            if (filtering && filtering.applyDataFilters) {
                result = filtering.applyDataFilters(result);
            }

            if (sorting && sorting.applySorting) {
                result = sorting.applySorting(result);
            }

            if (pagination && pagination.applyPagination) {
                const paginationResult = pagination.applyPagination(result);

                return {
                    filteredData: result,
                    currentPageData: paginationResult.paginatedData,
                    totalItems: paginationResult.totalItems,
                    totalPages: paginationResult.totalPages
                };
            }

            return {
                filteredData: result,
                currentPageData: result,
                totalItems: result.length,
                totalPages: 1
            };
        }
        catch (error) {
            logger.error('Error processing table data:', error);
            return {
                filteredData: [],
                currentPageData: [],
            }
        }
    }, [
        dataService?.dataRefreshTrigger,
        search?.searchValue,
        filtersRefreshTrigger,
        pagination?.currentPage,
        pagination?.pageSize,
        sorting?.sortColumn,
        sorting?.sortType,
        dateRange?.dateRange,
    ]);

    // Simple event handlers that coordinate between hooks
    const handleSearchChange = (searchTerm) => {
        try {
            search?.handleSearchChange?.(searchTerm);
            // Page reset is now handled by the search hook
        } catch (error) {
            logger.error('Error handling search change:', error);
        }
    };

    const handleSortChange = (dataKey, sortType) => {
        try {
            sorting?.handleSortColumn?.(dataKey, sortType);
            pagination?.resetToFirstPage?.();
        } catch (error) {
            logger.error('Error handling sort change:', error);
        }
    };

    const handleFiltersApply = () => {
        try {
            filtering?.applyFilters?.();
            // Page reset is already handled by the filtering hook
        } catch (error) {
            logger.error('Error applying filters:', error);
        }
    }; 
    
    const handleDateRangeChange = async (dates, from, to) => {
        try {
            // Update the date range first and get the formatted dates
            const formattedDates = await dateRange?.handleDateRangeChange?.(dates);

            // Use provided from/to or the formatted dates from the date range hook
            const dateRangeParams = from && to ? { from, to } : formattedDates;

            // Refresh the data with the new date range
            if (dateRangeParams && dateRangeParams.from && dateRangeParams.to) {
                await dataService?.refreshData?.(true, dateRangeParams);
            }

            // Reset pagination to first page
            pagination?.resetToFirstPage?.();
        } catch (error) {
            logger.error('Error handling date range change:', error);
        }
    };

    return {
        data: processedData.currentPageData || [],
        allData: processedData.filteredData || [],
        loading: dataService?.loading ?? true,
        totalItems: processedData.totalItems || 0,
        totalPages: processedData.totalPages || 0,
        sortColumn: sorting?.sortColumn || '',
        sortType: sorting?.sortType || 'asc',
        handleSortColumn: handleSortChange,

        // Pagination  
        currentPage: pagination?.currentPage || 1,
        pageSize: pagination?.pageSize || 10,
        handlePageChange: pagination?.handlePageChange || (() => { }),
        handlePageSizeChange: pagination?.handlePageSizeChange || (() => { }),
        searchTerm: search?.searchTerm || '',
        handleSearchChange,

        // Filtering
        activeFilters: filtering?.activeFilters || {},
        filtersRef: filtering?.filtersRef || { current: {} },
        applyFilters: handleFiltersApply,
        getColumnValues: filtering?.getColumnValues || (() => []),
        setFilterValues: filtering?.setFilterValues || (() => { }),
        getFilterValues: filtering?.getFilterValues || (() => []),       
        
        // Date Range
        dateRange: dateRange?.dateRange || null,
        setDateRange: dateRange?.setDateRange || (() => { }),
        handleDateRangeChange,
        getFormattedDateRange: dateRange?.getFormattedDateRange || (() => ({ from: null, to: null })),
        isDateRangeInitialized: dateRange?.isInitialized || !hasDateRange,

        // Data Management
        refreshData: dataService?.refreshData || (() => Promise.resolve()),
        loadInitialData: dataService?.loadInitialData || (() => Promise.resolve())
    };
}

export default useTableData;
